// src/pages/RecuperarSenha.js — "Esqueci minha senha" por código no e-mail (out/26).
//
// Antes o "Esqueceu sua senha?" do login só abria um modal mandando escrever
// pro suporte. Agora, em dois passos na mesma página:
//   1. e-mail → POST /auth/senha/esqueci. A resposta é a MESMA com e sem
//      conta (o servidor não revela quem é cliente), então a tela também não
//      diz "enviamos": diz "se tiver conta, enviamos".
//   2. código de 6 dígitos + senha nova → POST /auth/senha/redefinir, que
//      devolve o mesmo que o login: já entra. Os outros aparelhos saem.
// Regras do código (15 min, 5 tentativas, reenvio em 60 s) vivem no servidor
// (services/codigos_email.py); a tela só mostra o que ele responde.
//
// Mesmo visual do login (paleta, Syne, campos escuros), numa coluna só.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/Api';

const C = {
  bg: '#0A0F1A',
  bg2: '#0E1525',
  bg3: '#131C30',
  bg4: '#1A2540',
  bdr: 'rgba(255,255,255,0.07)',
  bdr2: 'rgba(255,255,255,0.12)',
  green: '#00D67C',
  greenBg: 'rgba(0,214,124,0.10)',
  greenB: 'rgba(0,214,124,0.28)',
  red: '#FF4560',
  redBg: 'rgba(255,69,96,0.12)',
  redB: 'rgba(255,69,96,0.30)',
  text: '#DDE4F0',
  text2: '#7A8799',
  text3: '#3E4D60',
};
const FONT = "'Syne','Segoe UI',sans-serif";
const SENHA_MINIMA = 8;

function mensagemDeErro(err, padrao) {
  const st = err?.response?.status;
  const d = err?.response?.data?.detail;
  if (st === 429) return 'Muitas tentativas seguidas. Aguarde alguns minutos e tente de novo.';
  if (typeof d === 'string') return d;
  if (!err?.response) return 'Não foi possível conectar. Verifique sua conexão.';
  return padrao;
}

function Campo({ id, rotulo, icone, children, dica }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label htmlFor={id} style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '.1em',
        textTransform: 'uppercase', color: C.text2,
      }}>
        {rotulo}
      </label>
      <div className="field-input" style={{
        background: C.bg4, border: `1px solid ${C.bdr2}`,
        borderRadius: 7, padding: '11px 13px',
        display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
        transition: 'border-color .2s, background .2s',
      }}>
        <span aria-hidden="true" style={{ fontSize: 14, color: C.text3, flexShrink: 0 }}>{icone}</span>
        {children}
      </div>
      {dica && <div style={{ fontSize: 11, color: C.text2 }}>{dica}</div>}
    </div>
  );
}

export default function RecuperarSenha() {
  const navigate = useNavigate();
  const location = useLocation();
  const [etapa, setEtapa] = useState('email');
  const [email, setEmail] = useState(location.state?.email || '');
  const [codigo, setCodigo] = useState('');
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [aviso, setAviso] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [reenviarEm, setReenviarEm] = useState(0);
  const campoCodigo = useRef(null);

  useEffect(() => {
    if (reenviarEm <= 0) return undefined;
    const t = setTimeout(() => setReenviarEm((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [reenviarEm]);

  useEffect(() => {
    if (etapa === 'codigo') campoCodigo.current?.focus();
  }, [etapa]);

  const pedirCodigo = async (e) => {
    e?.preventDefault();
    if (enviando || !email.trim()) return;
    setEnviando(true);
    setErro('');
    try {
      const { data } = await api.post('/auth/senha/esqueci', { email: email.trim() });
      setAviso(data.mensagem);
      setReenviarEm(data.reenviar_em || 60);
      setCodigo('');
      setEtapa('codigo');
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível enviar o código. Tente de novo.'));
    } finally {
      setEnviando(false);
    }
  };

  const so6 = codigo.replace(/\D/g, '');
  const senhaCurta = senha.length > 0 && senha.length < SENHA_MINIMA;
  const naoConfere = confirma.length > 0 && confirma !== senha;
  const podeTrocar = so6.length === 6 && senha.length >= SENHA_MINIMA && confirma === senha && !enviando;

  const trocarSenha = async (e) => {
    e.preventDefault();
    if (!podeTrocar) return;
    setEnviando(true);
    setErro('');
    try {
      const { data } = await api.post('/auth/senha/redefinir', {
        email: email.trim(), codigo: so6, nova_senha: senha,
      });
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('company_id', data.user.company_id || '');
      localStorage.setItem('company_name', data.user.company_name || '');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível trocar a senha. Tente de novo.'));
      setEnviando(false);
    }
  };

  const botao = (desabilitado) => ({
    width: '100%', padding: 13, borderRadius: 7,
    background: desabilitado ? C.text3 : C.green, color: '#0A0F1A',
    border: 'none', fontFamily: FONT, fontSize: 14, fontWeight: 700,
    cursor: desabilitado ? 'not-allowed' : 'pointer', opacity: desabilitado ? 0.6 : 1,
    boxShadow: desabilitado ? 'none' : '0 4px 16px rgba(0,214,124,.25)',
    transition: 'all .18s', marginTop: 20,
  });

  return (
    <>
      <style>{`
        .recuperar-v2 * { box-sizing: border-box; }
        .recuperar-v2 .field-input:focus-within { border-color: ${C.greenB}; background: rgba(0,214,124,0.04); }
        .recuperar-v2 input {
          background: transparent; border: none; outline: none;
          color: ${C.text}; font-family: ${FONT}; font-size: 13px;
          flex: 1; min-width: 0; padding: 0;
        }
        .recuperar-v2 input::placeholder { color: ${C.text3}; font-size: 12px; }
        .recuperar-v2 input:-webkit-autofill,
        .recuperar-v2 input:-webkit-autofill:hover,
        .recuperar-v2 input:-webkit-autofill:focus {
          -webkit-text-fill-color: ${C.text};
          -webkit-box-shadow: 0 0 0px 1000px ${C.bg4} inset;
          transition: background-color 5000s ease-in-out 0s;
        }
        .recuperar-v2 .codigo { font-size: 22px; letter-spacing: 10px; font-variant-numeric: tabular-nums; }
        .recuperar-v2 .codigo::placeholder { font-size: 22px; letter-spacing: 10px; }
        .recuperar-v2 .link { background: none; border: none; padding: 0; font-family: ${FONT};
          font-size: 12px; color: ${C.text2}; cursor: pointer; transition: color .15s; }
        .recuperar-v2 .link:hover:not(:disabled) { color: ${C.green}; }
        .recuperar-v2 .link:disabled { cursor: default; color: ${C.text3}; }
        .recuperar-v2 button:focus-visible, .recuperar-v2 .pw-eye:focus-visible {
          outline: 2px solid ${C.green}; outline-offset: 2px;
        }
      `}</style>

      <div className="recuperar-v2" style={{ minHeight: '100vh', background: C.bg2, color: C.text, fontFamily: FONT }}>
        <div style={{
          height: 58, padding: '0 24px', borderBottom: `1px solid ${C.bdr}`, background: C.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
              <span style={{ color: C.green }}>C</span>ETTO
            </div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.2em', color: C.text3, textTransform: 'uppercase', marginTop: -2 }}>
              System
            </div>
          </div>
          <button type="button" className="link" onClick={() => navigate('/login')}>
            Lembrou a senha? <span style={{ color: C.green, fontWeight: 600 }}>Entrar →</span>
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 16px' }}>
          <div style={{ width: '100%', maxWidth: 400 }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', letterSpacing: '-.5px', margin: '0 0 6px 0' }}>
              {etapa === 'email' ? 'Recuperar acesso' : 'Digite o código'}
            </h1>
            <p style={{ fontSize: 13, color: C.text2, lineHeight: 1.6, margin: 0 }}>
              {etapa === 'email'
                ? 'Digite o e-mail da sua conta. Enviamos um código de 6 dígitos para você criar uma senha nova.'
                : <>{aviso} <span style={{ color: C.text }}>({email.trim()})</span></>}
            </p>

            <div style={{ background: C.bg3, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: '24px 24px 20px', marginTop: 24 }}>
              <div aria-live="polite">
                {erro && (
                  <div role="alert" style={{
                    display: 'flex', alignItems: 'flex-start', gap: 9,
                    background: C.redBg, border: `1px solid ${C.redB}`,
                    borderRadius: 7, padding: '10px 13px', marginBottom: 16,
                  }}>
                    <span aria-hidden="true" style={{ fontSize: 14, color: C.red, flexShrink: 0 }}>⚠</span>
                    <span style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{erro}</span>
                  </div>
                )}
              </div>

              {etapa === 'email' && (
                <form onSubmit={pedirCodigo}>
                  <Campo id="rec-email" rotulo="E-mail da conta" icone="✉">
                    <input
                      id="rec-email" type="email" name="email" value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@empresa.com.br" autoComplete="username" required
                      disabled={enviando}
                    />
                  </Campo>
                  <button type="submit" disabled={enviando || !email.trim()} style={botao(enviando || !email.trim())}>
                    {enviando ? 'Enviando…' : 'Enviar código'}
                  </button>
                </form>
              )}

              {etapa === 'codigo' && (
                <form onSubmit={trocarSenha}>
                  {/* Pro gerenciador de senhas salvar a nova na conta certa. */}
                  <input type="email" name="email" value={email} autoComplete="username" readOnly hidden />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Campo id="rec-codigo" rotulo="Código do e-mail" icone="#">
                      <input
                        id="rec-codigo" ref={campoCodigo} className="codigo" name="codigo"
                        value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/[^\d ]/g, '').slice(0, 7))}
                        inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                        aria-describedby="rec-codigo-dica" disabled={enviando}
                      />
                    </Campo>
                    <div id="rec-codigo-dica" style={{ fontSize: 11, color: C.text2, marginTop: -8 }}>
                      Vale por 15 minutos. Não chegou? Confira o spam.
                    </div>

                    <Campo
                      id="rec-senha" rotulo="Senha nova" icone="🔒"
                      dica={senhaCurta ? `Faltam ${SENHA_MINIMA - senha.length} caracteres.` : `Mínimo ${SENHA_MINIMA} caracteres.`}
                    >
                      <input
                        id="rec-senha" type={mostrar ? 'text' : 'password'} name="new-password"
                        value={senha} onChange={(e) => setSenha(e.target.value)}
                        placeholder="••••••••" autoComplete="new-password" disabled={enviando}
                      />
                      <span
                        className="pw-eye" role="button" tabIndex={0}
                        aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
                        onClick={() => setMostrar((v) => !v)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMostrar((v) => !v); } }}
                        style={{ fontSize: 14, color: C.text3, cursor: 'pointer', flexShrink: 0, userSelect: 'none' }}
                      >
                        {mostrar ? '🙈' : '👁'}
                      </span>
                    </Campo>

                    <Campo
                      id="rec-confirma" rotulo="Repita a senha nova" icone="🔒"
                      dica={naoConfere ? 'As duas senhas não estão iguais.' : null}
                    >
                      <input
                        id="rec-confirma" type={mostrar ? 'text' : 'password'} name="confirm-password"
                        value={confirma} onChange={(e) => setConfirma(e.target.value)}
                        placeholder="••••••••" autoComplete="new-password" disabled={enviando}
                      />
                    </Campo>
                  </div>

                  <button type="submit" disabled={!podeTrocar} style={botao(!podeTrocar)}>
                    {enviando ? 'Trocando…' : 'Trocar senha e entrar'}
                  </button>

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                    <button type="button" className="link" disabled={reenviarEm > 0 || enviando} onClick={pedirCodigo}>
                      {reenviarEm > 0 ? `Reenviar código em ${reenviarEm} s` : 'Reenviar código'}
                    </button>
                    <button type="button" className="link" disabled={enviando}
                      onClick={() => { setEtapa('email'); setErro(''); setCodigo(''); }}>
                      Usar outro e-mail
                    </button>
                  </div>
                </form>
              )}
            </div>

            <p style={{ fontSize: 11, color: C.text2, lineHeight: 1.6, textAlign: 'center', marginTop: 16 }}>
              Ao trocar a senha, todos os aparelhos conectados com a senha antiga saem da conta.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
